import { Breadcrumb, Layout, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ColumnFilterItem } from 'antd/lib/table/interface';
import _ from 'lodash';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { IDictionary } from '~utilities/IDictionary';
import { loansDrawdownsPendingListAction } from '~/Loans/actions';
import { loansDrawdownsPendingSelector } from '~/Loans/selectors';
import { currencyFormatter } from '~utilities/formatters';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import loanDrawdownStatusLabels from './loanDrawdownStatusLabels';
import ILoan from '~Api/Loan/ILoan';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import LoanDrawdownStatusTag from './LoanDrawdownStatusTag';

export default function PendingDrawdowns(): ReactElement {
    const drawdowns: IDictionary<ILoanDrawdown> = useSelector(loansDrawdownsPendingSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!drawdowns) {
            dispatch(loansDrawdownsPendingListAction());
        }
    }, [
        dispatch,
        drawdowns,
    ]);

    if (!drawdowns) {
        return (
            <Layout className='loan-drawdowns'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Pending Drawdowns</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Pending Drawdowns</Typography.Title>
                        <Spin/>
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const columns: ColumnsType<ILoanDrawdown> = [
        {
            dataIndex: 'loan',
            render: (loan: ILoan) => <Link to={`/loans/${loan.uuid}/drawdowns`}>{loan.code}</Link>,
            title: 'Loan',
            width: '10%',
        },
        {
            dataIndex: 'loan',
            defaultSortOrder: 'ascend',
            render: (loan: ILoan) => <Link to={`/loans/${loan.uuid}/drawdowns`}>{loan.name}</Link>,
            sorter: (a: ILoanDrawdown, b: ILoanDrawdown) => (a.loan.name || '').localeCompare(b.loan.name || ''),
            title: 'Name',
        },
        {
            dataIndex: 'drawdownNumber',
            render: (drawdownNumber: string, loanDrawdown: ILoanDrawdown) => <Link to={`/loans/${loanDrawdown.loanUuid}/drawdowns/${loanDrawdown.uuid}`}>{drawdownNumber}</Link>,
            title: 'Number',
            width: '6%',
        },
        {
            dataIndex: 'requestedAmount',
            render: (requestedAmount: number) => currencyFormatter.format(requestedAmount),
            title: 'Requested Amount',
            width: '15%',
        },
        {
            dataIndex: 'approvedDrawdownAmount',
            render: (approvedDrawdownAmount: number) => approvedDrawdownAmount ? currencyFormatter.format(approvedDrawdownAmount) : '-',
            title: 'Approved Amount',
            width: '15%',
        },
        {
            filters: _.without(
                _.keys(loanDrawdownStatusLabels),
                LoanDrawdownStatusEnum.Cancelled,
                LoanDrawdownStatusEnum.Processed,
            ).map((status: string): ColumnFilterItem => ({
                text: loanDrawdownStatusLabels[status],
                value: status,
            })),
            onFilter: (value: string | number | boolean, loanDrawdown: ILoanDrawdown) => loanDrawdown.status === value,
            render: (loanDrawdown: ILoanDrawdown) => <LoanDrawdownStatusTag loanDrawdown={loanDrawdown} />,
            title: 'Status',
            width: '15%',
        },
    ];

    return (
        <Layout className='loans'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Pending Drawdowns</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Typography.Title level={2}>Pending Drawdowns</Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={_.values(drawdowns)}
                        pagination={false}
                        rowKey='uuid'
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
        </Layout>
    );
}
